/** REST request/response DTOs for /v1. The API package wraps these with @hono/zod-openapi routes. */
import { z } from "zod";
import { AccessPolicy, AgentProfile, Channel, ChannelKind, ChannelMembership, GifRef, Id, Member, Message, Organization, Role, SidebarFolder, SidebarItem, Timestamp, Workspace } from "./entities.ts";
import { EventEnvelope } from "./realtime.ts";
import { PageNode } from "./docs.ts";
import { Huddle } from "./huddles.ts";
import { UserGroup, UserGroupPolicy } from "./userGroups.ts";

export const BootstrapResponse = z.object({
  workspace: Workspace,
  me: Member,
  members: z.array(Member),
  /** Channels visible to the caller (joined channels + public channels). */
  channels: z.array(Channel),
  memberships: z.array(ChannelMembership),
  /** Page tree for the sidebar (pages + databases the caller can see; rows excluded). Absent from pre-M5 servers. */
  pages: z.array(PageNode).optional(),
  favoritePageIds: z.array(Id).optional(),
  /** Active huddles in conversations the caller belongs to (absent when there are none). */
  huddles: z.array(Huddle).optional(),
  /** My sidebar folders (absent when I have none). */
  sidebarFolders: z.array(SidebarFolder).optional(),
  /** My sidebar placements: items in folders or ordered by hand (absent when none). */
  sidebarItems: z.array(SidebarItem).optional(),
  /** Current workspace event seq; pass as hello.lastSeq when connecting. */
  seq: z.number().int(),
  /**
   * This server can write AI thread summaries (a model gateway key is configured). Absent ⇒ no: clients hide the
   * TL;DR and the Summarize action. Whether the workspace allows them is `workspace.aiThreadSummaries`.
   */
  aiAvailable: z.boolean().optional(),
  /**
   * Mail, Contacts and Calendar are on for this workspace (server var MAIL_WORKSPACES). Absent ⇒ off: clients hide
   * them (sidebar, tab bar, Settings → Mail) and their routes answer 404.
   */
  mail: z.boolean().optional(),
  /** User groups (userGroups.ts), disabled ones included; absent when there are none. */
  userGroups: z.array(UserGroup).optional(),
  /** Who can manage user groups; absent ⇒ "everyone". */
  userGroupPolicy: UserGroupPolicy.optional(),
  /** People I blocked in this workspace (moderation.ts); absent (older servers and caches, or nobody) ⇒ []. */
  blockedUserIds: z.array(Id).optional(),
  /**
   * The request's country (ISO 3166-1 alpha-2) as Cloudflare reports it (`request.cf.country`); absent when unknown
   * (local development, Tor, older servers). Clients use it only for the usage-analytics default
   * (core logic/analyticsConsent): ask first where prior consent is required, on elsewhere. Not stored or logged.
   */
  country: z.string().length(2).optional(),
});
export type BootstrapResponse = z.infer<typeof BootstrapResponse>;

export const ListMessagesQuery = z.object({
  before: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  threadRootId: Id.optional(),
});
export const ListMessagesResponse = z.object({ messages: z.array(Message), hasMore: z.boolean() });
export type ListMessagesResponse = z.infer<typeof ListMessagesResponse>;

export const PostMessageRequest = z.object({
  /** Markdown-ish text; may be empty only when `fileIds` attaches files (it is then their caption), and must be empty with `stickerId` / `gif`. */
  text: z.string().max(40_000),
  threadRootId: Id.optional(),
  alsoSendToChannel: z.boolean().optional(),
  clientId: z.string().max(64).optional(),
  /** Offline-queued sends: when the client composed the message (metadata only; server seq decides order). */
  clientCreatedAt: z.number().int().nonnegative().optional(),
  /**
   * Ready uploads (files.ts) to attach, in display order (max 10). Each must have been uploaded by the poster for this
   * channel and not be attached yet; the message's `files` (and its `message.created` event) carry them.
   */
  fileIds: z.array(Id).max(10).optional(),
  /**
   * Sends a sticker (stickers.ts): any sticker of the workspace by id. The message has no text and no files; it
   * carries the sticker's `StickerRef` as it is now.
   */
  stickerId: Id.optional(),
  /** Sends a GIF from the provider (no text, no files). Its URLs must be https on the provider's media host. */
  gif: GifRef.optional(),
});
export type PostMessageRequest = z.infer<typeof PostMessageRequest>;
export const MessageResponse = z.object({ message: Message });

export const EditMessageRequest = z.object({ text: z.string().min(1).max(40_000) });
export const ReactionRequest = z.object({ emoji: z.string().min(1).max(64) });

/**
 * Following a thread ("Get notified about new replies", like Slack): followers get an Activity item and a notification
 * for each new reply. Starting a thread (being the root's author when the first reply comes), replying, or being
 * @-mentioned in a reply follows it; anyone in the conversation can follow or unfollow any message. Per person.
 */
export const ThreadFollowResponse = z.object({ following: z.boolean() });
export type ThreadFollowResponse = z.infer<typeof ThreadFollowResponse>;

/** PUT …/messages/:messageId/follow — follow (true) or unfollow (false) a thread root. */
export const SetThreadFollowRequest = z.object({ following: z.boolean() });
export type SetThreadFollowRequest = z.infer<typeof SetThreadFollowRequest>;

export const CreateChannelRequest = z.object({
  kind: ChannelKind,
  name: z.string().max(80).default(""),
  topic: z.string().max(250).optional(),
  memberIds: z.array(Id).max(500).optional(),
  /**
   * Public/private channels: a client-generated ULID, so an optimistic (offline-queued) create already has its final
   * id. Replays with the same id by the same creator return the channel; an id used by anything else is a conflict.
   */
  id: Id.optional(),
  /** Description ("what is this channel about"). */
  purpose: z.string().max(250).optional(),
});
export type CreateChannelRequest = z.infer<typeof CreateChannelRequest>;
export const ChannelResponse = z.object({ channel: Channel });

export const MarkReadRequest = z.object({ seq: z.number().int().nonnegative() });

/** REST event catch-up for clients without a live socket (background refresh, push-triggered sync). */
export const EventsQuery = z.object({
  after: z.coerce.number().int().nonnegative(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export const EventsResponse = z.object({
  /** Audience-filtered events with seq > after, ascending. */
  events: z.array(EventEnvelope),
  /** Current workspace event seq. */
  seq: z.number().int(),
  /** `after` is outside the replay window — call bootstrap instead. */
  resync: z.boolean(),
  /** More events remain after the last one returned (page again with after = last seq). */
  hasMore: z.boolean(),
});
export type EventsResponse = z.infer<typeof EventsResponse>;

export const RealtimeTicketResponse = z.object({ ticket: z.string(), url: z.string(), expiresAt: z.number().int() });

export const ApiError = z.object({ error: z.object({ code: z.string(), message: z.string() }) });
export type ApiError = z.infer<typeof ApiError>;

export const DevLoginRequest = z.object({ email: z.string().email(), name: z.string().max(80).optional() });
export const DevLoginResponse = z.object({ token: z.string(), userId: Id, workspaceId: Id, expiresAt: z.number().int() });
export type DevLoginResponse = z.infer<typeof DevLoginResponse>;

/**
 * POST /v1/auth/signup/check (no auth): whether an email already belongs to an account (its primary or a verified sign-in
 * email), so Create account can say "You already have an account" before sending a code, like Slack. Rate-limited per IP
 * (JOIN_RATE_LIMIT, 429 `rate_limited`).
 */
export const SignupCheckRequest = z.object({ email: z.string().trim().email().max(254) });
export type SignupCheckRequest = z.infer<typeof SignupCheckRequest>;
export const SignupCheckResponse = z.object({ exists: z.boolean() });
export type SignupCheckResponse = z.infer<typeof SignupCheckResponse>;
export const MyWorkspacesResponse = z.object({ workspaces: z.array(Workspace) });

// ---------- Guests & agents (same scoped-access model) ----------

export const InviteGuestRequest = z.object({
  email: z.string().email(),
  name: z.string().max(80).optional(),
  organization: Organization,
  /** Channels the guest is added to (their whole visible world, plus pageIds). */
  channelIds: z.array(Id).min(1),
  pageIds: z.array(Id).optional(),
  expiresAt: z.number().int().optional(),
  /** Overrides on top of the restricted-guest defaults. */
  can: AccessPolicy.shape.can.partial().optional(),
});
export type InviteGuestRequest = z.infer<typeof InviteGuestRequest>;

export const CreateAgentRequest = z.object({
  displayName: z.string().min(1).max(80),
  description: z.string().max(500).optional(),
  runtime: AgentProfile.shape.runtime,
  model: z.string().optional(),
  channelIds: z.array(Id).default([]),
  /** Defaults to restricted with the caller as sponsor. */
  access: AccessPolicy.optional(),
});
export type CreateAgentRequest = z.infer<typeof CreateAgentRequest>;

export const UpdateAccessRequest = z.object({ access: AccessPolicy });
export const MemberResponse = z.object({ member: Member });
export type MemberResponse = z.infer<typeof MemberResponse>;
/** Agent tokens are shown once at creation (external runtime); hosted agents get none. */
export const CreateAgentResponse = z.object({ member: Member, token: z.string().optional() });
export type CreateAgentResponse = z.infer<typeof CreateAgentResponse>;

// ---------- Desktop sign-in through the browser (PKCE + bundle:// deep link) ----------
// 1. Desktop opens  <web>/desktop-auth?challenge=<S256(verifier)>&state=<random>&device=<name>  in the system browser.
// 2. The signed-in web page asks the user to confirm, then POST desktopAuthCode → { code } and redirects to
//    bundle://auth/callback?code=<code>&state=<state>.
// 3. The desktop app (Rust side) checks state and POST desktopAuthToken { code, codeVerifier } → its own session.

export const DesktopAuthCodeRequest = z.object({
  /** base64url(SHA-256(codeVerifier)), 43 chars. */
  codeChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  /** Shown in the user's session list, e.g. "Bundle for Mac — Ana's MacBook Pro". */
  deviceName: z.string().min(1).max(120),
});
export type DesktopAuthCodeRequest = z.infer<typeof DesktopAuthCodeRequest>;
/** Single-use, ~2 minute code bound to the challenge and the signed-in user. */
export const DesktopAuthCodeResponse = z.object({ code: z.string(), expiresAt: z.number().int() });
export type DesktopAuthCodeResponse = z.infer<typeof DesktopAuthCodeResponse>;

export const DesktopAuthTokenRequest = z.object({
  code: z.string().min(16).max(200),
  /** RFC 7636 code verifier, 43–128 chars. */
  codeVerifier: z.string().regex(/^[A-Za-z0-9._~-]{43,128}$/),
});
export type DesktopAuthTokenRequest = z.infer<typeof DesktopAuthTokenRequest>;
/** A NEW session for the desktop device (not the browser's): long-lived session token + first access token. */
export const DesktopAuthTokenResponse = z.object({
  sessionToken: z.string(),
  accessToken: z.string(),
  accessTokenExpiresAt: z.number().int(),
  userId: Id,
});
export type DesktopAuthTokenResponse = z.infer<typeof DesktopAuthTokenResponse>;

// ---------- Sign-in methods ----------

/**
 * Which sign-in methods this deployment offers, so clients only show what works: Google when the OAuth client is
 * configured, Apple when its Services ID, team, key ID and signing key are configured, email codes when outgoing
 * email is configured, dev login only in local development. `apple` is absent from servers older than Apple sign-in
 * (treat as false).
 */
export const AuthMethodsResponse = z.object({
  google: z.boolean(),
  apple: z.boolean().optional(),
  emailCode: z.boolean(),
  devLogin: z.boolean(),
  /**
   * The request's country (ISO 3166-1 alpha-2) as Cloudflare reports it (`request.cf.country`); absent when unknown
   * (local development, Tor, older servers). Clients use it only for the usage-analytics default
   * (core logic/analyticsConsent): ask first where prior consent is required, on elsewhere. Not stored or logged.
   */
  country: z.string().length(2).optional(),
});
export type AuthMethodsResponse = z.infer<typeof AuthMethodsResponse>;

/**
 * GET /v1/version (no auth): the deployed API build, for About screens and support. `commit` is the git sha the
 * Worker was deployed from and `builtAt` when (ISO 8601); both null on a local or hand-run deploy that didn't set them.
 */
export const VersionResponse = z.object({
  service: z.literal("bundle-api"),
  commit: z.string().nullable(),
  builtAt: z.string().nullable(),
});
export type VersionResponse = z.infer<typeof VersionResponse>;

// ---------- Linked sign-in methods (settings → "Sign-in methods") ----------
// Linking and unlinking go through Better Auth (docs/auth/sign-in.md "Account linking"):
//   POST /auth/link-social { provider, callbackURL, errorCallbackURL, disableRedirect: true } → { url } (web redirect)
//   POST /auth/link-social { provider, idToken: { token, nonce? } } → { status: true }              (native)
//   POST /auth/unlink-account { accountId }  (refused with LAST_SIGN_IN_METHOD when nothing would remain)
//   POST /auth/email-otp/request-email-change { newEmail } → POST /auth/email-otp/change-email { newEmail, otp }
// The list below is a /v1 route because it adds what Better Auth's /auth/list-accounts lacks: each provider's email.

/** Providers that can be linked to an account (email codes need no link: they follow the account's address). */
export const LinkedProvider = z.enum(["google", "apple"]);
export type LinkedProvider = z.infer<typeof LinkedProvider>;

export const LinkedAccount = z.object({
  /** Better Auth account id: POST /auth/unlink-account { accountId: id }. */
  id: Id,
  provider: LinkedProvider,
  /** The address this provider identifies the person with (absent when it shared none). */
  email: z.string().optional(),
  /** An Apple "Hide My Email" relay address (…@privaterelay.appleid.com). */
  privateRelay: z.boolean(),
  /** The provider has a profile photo that can become the avatar (POST /v1/auth/accounts/:id/photo). */
  hasPhoto: z.boolean(),
  linkedAt: Timestamp,
});
export type LinkedAccount = z.infer<typeof LinkedAccount>;

export const SignInMethodsResponse = z.object({
  /** The account's address (sign-in codes go here). Absent when Apple shared no address at all. */
  email: z.string().optional(),
  /**
   * Email codes sign in to this account: the deployment sends email and the account's address is a verified one the
   * person can type (not an Apple relay address). Otherwise clients offer "Add email sign-in".
   */
  emailCode: z.boolean(),
  /** Linked providers, oldest first. */
  accounts: z.array(LinkedAccount),
});
export type SignInMethodsResponse = z.infer<typeof SignInMethodsResponse>;

/** "Use Google photo": how many of my workspace profiles now show it. */
export const UseProviderPhotoResponse = z.object({ workspaces: z.number().int().nonnegative() });
export type UseProviderPhotoResponse = z.infer<typeof UseProviderPhotoResponse>;

// ---------- Workspaces & invites ----------

export const CreateWorkspaceRequest = z.object({
  /** Display name (trimmed, 1–80 chars). The URL slug is derived from it (core `logic/slug`) and made unique. */
  name: z.string().min(1).max(80),
});
export type CreateWorkspaceRequest = z.infer<typeof CreateWorkspaceRequest>;
/** The new workspace; the creator is its owner and a member of the default channels (#general, #random). */
export const CreateWorkspaceResponse = z.object({ workspace: Workspace, generalChannelId: Id });
export type CreateWorkspaceResponse = z.infer<typeof CreateWorkspaceResponse>;

/** Roles an invite can grant. Guests are invited with InviteGuestRequest (scoped access) instead. */
export const InviteRole = z.enum(["member", "admin"]);
export type InviteRole = z.infer<typeof InviteRole>;

export const CreateInvitesRequest = z.object({
  /** One single-use invite per address (accepted only by that email). Absent/empty → one shareable link. */
  emails: z.array(z.string().email()).max(50).optional(),
  role: InviteRole.default("member"),
});
export type CreateInvitesRequest = z.infer<typeof CreateInvitesRequest>;

/** An invite as returned to its creator. The token (and the URL that carries it) is shown only once. */
export const Invite = z.object({
  id: Id,
  /** Opaque secret; only its SHA-256 is stored. */
  token: z.string(),
  /** Shareable web link: <web origin>/invite/<token>. */
  url: z.string(),
  role: Role,
  /** Present for email invites (single use, bound to this address); absent for shareable links (multi-use). */
  email: z.string().optional(),
  expiresAt: Timestamp,
  /** The invite email was handed to the mail service (false when email isn't configured or for links). */
  emailed: z.boolean(),
});
export type Invite = z.infer<typeof Invite>;
export const CreateInvitesResponse = z.object({ invites: z.array(Invite) });
export type CreateInvitesResponse = z.infer<typeof CreateInvitesResponse>;

/** Public preview of an invite (no auth): just enough to render "Join <workspace>". */
export const InvitePreviewWorkspace = z.object({ name: z.string(), iconUrl: z.string().optional() });
export const InvitePreviewResponse = z.object({ workspace: InvitePreviewWorkspace, inviterName: z.string() });
export type InvitePreviewResponse = z.infer<typeof InvitePreviewResponse>;

/** Joined (or already a member — accepting twice is harmless). */
export const AcceptInviteResponse = z.object({ workspace: Workspace, alreadyMember: z.boolean() });
export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponse>;

/** Guests get a sign-in link too (bound to the guest's email). */
export const InviteGuestResponse = z.object({ member: Member, invite: Invite.optional() });
export type InviteGuestResponse = z.infer<typeof InviteGuestResponse>;

// ---------- Push devices (FCM registration tokens for the notify queue) ----------

/** Where the token came from. `desktop` is recorded but not pushed to (desktop alerts come from the socket). */
export const DevicePlatform = z.enum(["ios", "android", "web", "desktop"]);
export type DevicePlatform = z.infer<typeof DevicePlatform>;

/**
 * Registers (or refreshes) this device's push token for the signed-in person. Idempotent upsert keyed by the token:
 * a token registered by someone else before (shared device, missed sign-out) moves to the caller. Send it after
 * sign-in, whenever the token changes, and now and then (FCM drops tokens unused for ~270 days).
 */
export const RegisterDeviceRequest = z.object({
  platform: DevicePlatform,
  /** FCM registration token (iOS/Android SDK, or firebase/messaging getToken on the web). */
  token: z.string().min(1).max(4096),
  /** BCP 47 app language, so server-rendered notification text matches it ("pt-BR"). */
  locale: z.string().max(35).optional(),
  /** "0.1.0 (12)". */
  appVersion: z.string().max(64).optional(),
  /** IANA zone (DND schedules). */
  timeZone: z.string().max(64).optional(),
  /**
   * iOS only: this device's PushKit VoIP token (hex). Huddle rings then go to it as VoIP pushes sent straight to APNs
   * (CallKit rings with the app closed), when the API has an APNs auth key; otherwise they stay FCM alerts.
   */
  voipToken: z.string().min(1).max(512).optional(),
});
export type RegisterDeviceRequest = z.infer<typeof RegisterDeviceRequest>;
